import React, { useState, useEffect, useCallback } from 'react';
import { secureStorage } from '../utils/security';
import './RightSidebar.css';

const RightSidebar = ({ walletAddress }) => {
  const [followingList, setFollowingList] = useState([]);
  const [followingProfiles, setFollowingProfiles] = useState({});

  // Load following list directly from secureStorage using walletAddress prop
  const loadFollowing = useCallback(() => {
    if (!walletAddress) return;
    try {
      const raw = secureStorage.getItem(`znoospace_following_${walletAddress}`);
      if (raw) {
        const list = typeof raw === 'string' ? JSON.parse(raw) : raw;
        setFollowingList(Array.isArray(list) ? list : []);
      } else {
        setFollowingList([]);
      }
      
      // Try to resolve display names from profile index
      try {
        const profileRaw = secureStorage.getItem('znoospace_user_profiles');
        if (profileRaw) {
          const profiles = typeof profileRaw === 'string' ? JSON.parse(profileRaw) : profileRaw;
          if (profiles && typeof profiles === 'object') {
            setFollowingProfiles(profiles);
          }
        }
      } catch {}
    } catch (e) {
      console.warn('Error loading following list:', e);
    }
  }, [walletAddress]);

  useEffect(() => {
    loadFollowing();
    // Listen for follow/unfollow events instead of polling every 3s
    const handleFollowChange = () => loadFollowing();
    window.addEventListener('znoospace_follow_change', handleFollowChange);
    // Also listen for storage changes (cross-tab sync)
    const handleStorage = (e) => {
      if (e.key && e.key.includes('znoospace_following_')) loadFollowing();
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('znoospace_follow_change', handleFollowChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, [loadFollowing]);

  const handleUnfollow = (addr) => {
    if (!walletAddress) return;
    try {
      const key = `znoospace_following_${walletAddress}`;
      const raw = secureStorage.getItem(key);
      const current = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : [];
      const updated = Array.isArray(current) ? current.filter(a => a !== addr) : [];
      secureStorage.setItem(key, JSON.stringify(updated));
      setFollowingList(updated);
      window.dispatchEvent(new Event('znoospace_follow_change'));
    } catch (e) {
      console.error('Error unfollowing:', e);
    }
  };

  const getProfileName = (addr) => {
    const profile = followingProfiles[addr];
    if (profile?.displayName && !profile.displayName.startsWith('User ')) return profile.displayName;
    return `User ${addr.slice(0, 8)}`;
  };
  const trendingTopics = [
    { topic: '#Solana', posts: '125K posts' },
    { topic: '#DeFi', posts: '89K posts' },
    { topic: '#Web3', posts: '67K posts' },
    { topic: '#Znoospace', posts: '12K posts' },
    { topic: '#Blockchain', posts: '156K posts' },
  ];

  const suggestedUsers = [
    { name: 'Solana Foundation', handle: '@solana', verified: true },
    { name: 'Phantom Wallet', handle: '@phantom', verified: true },
    { name: 'Solflare', handle: '@solflare_wallet', verified: true },
  ];

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  return (
    <div className="right-sidebar">
      {/* Search Box */}
      <div className="search-container">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input 
            type="text" 
            placeholder="Search Znoospace" 
            className="search-input"
          />
        </div>
      </div>

      {/* Deployment Status Card */}
      <div className="info-card">
        <h3>🚀 Znoospace Status</h3>
        <div className="status-items">
          <div className="status-item">
            <span className="status-label">Program ID:</span>
            <a 
              href="https://explorer.solana.com/address/6eYBoAEeB5VXEfSGmkejDfTmmBxeqDyV5QWr19eF9Zst?cluster=devnet"
              target="_blank"
              rel="noopener noreferrer"
              className="status-value link"
              title="View Program on Solana Explorer"
            >
              6eYBoA...F9Zst 🔗
            </a>
          </div>
          <div className="status-item">
            <span className="status-label">Network:</span>
            <span className="status-value">Solana Devnet</span>
          </div>
          <div className="status-item">
            <span className="status-label">Status:</span>
            <span className="status-value success">✅ Deployed</span>
          </div>
          {walletAddress && (
            <div className="status-item">
              <span className="status-label">Your Wallet:</span>
              <a 
                href={`https://explorer.solana.com/address/${walletAddress}?cluster=devnet`}
                target="_blank"
                rel="noopener noreferrer"
                className="status-value link"
                title="View Wallet on Solana Explorer"
              >
                {formatAddress(walletAddress)} 🔗
              </a>
            </div>
          )}
        </div>
      </div>

      {/* On-Chain Activity */}
      {walletAddress && (
        <div className="info-card">
          <h3>🔗 On-Chain Activity</h3>
          <div className="activity-items">
            <div className="activity-item">
              <div className="activity-icon">📝</div>
              <div className="activity-info">
                <div className="activity-type">Post Created</div>
                <div className="activity-time">2 minutes ago</div>
              </div>
              <a 
                href="https://explorer.solana.com/tx/example?cluster=devnet"
                target="_blank"
                rel="noopener noreferrer"
                className="activity-link"
                title="View Transaction"
              >
                🔗
              </a>
            </div>
            <div className="activity-item">
              <div className="activity-icon">❤️</div>
              <div className="activity-info">
                <div className="activity-type">Post Liked</div>
                <div className="activity-time">5 minutes ago</div>
              </div>
              <a 
                href="https://explorer.solana.com/tx/example2?cluster=devnet"
                target="_blank"
                rel="noopener noreferrer"
                className="activity-link"
                title="View Transaction"
              >
                🔗
              </a>
            </div>
            <div className="activity-item">
              <div className="activity-icon">👤</div>
              <div className="activity-info">
                <div className="activity-type">Profile Updated</div>
                <div className="activity-time">1 hour ago</div>
              </div>
              <a 
                href="https://explorer.solana.com/tx/example3?cluster=devnet"
                target="_blank"
                rel="noopener noreferrer"
                className="activity-link"
                title="View Transaction"
              >
                🔗
              </a>
            </div>
          </div>
          <div className="show-more">
            <a 
              href={`https://explorer.solana.com/address/${walletAddress}?cluster=devnet`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View all transactions
            </a>
          </div>
        </div>
      )}

      {/* Following Section */}
      {walletAddress && (
        <div className="info-card">
          <h3>👥 Following <span className="following-count">{followingList.length}</span></h3>
          {followingList.length === 0 ? (
            <div className="following-empty">
              <p>You're not following anyone yet.</p>
              <p className="following-hint">Use Explore to discover users!</p>
            </div>
          ) : (
            <div className="following-list">
              {followingList.map((addr) => (
                <div key={addr} className="following-item">
                  <div className="following-avatar">🚀</div>
                  <div className="following-info">
                    <div className="following-name">{getProfileName(addr)}</div>
                    <a
                      href={`https://explorer.solana.com/address/${addr}?cluster=devnet`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="following-address"
                      title={addr}
                    >
                      {addr.slice(0, 4)}...{addr.slice(-4)} 🔗
                    </a>
                  </div>
                  <button
                    className="unfollow-btn"
                    onClick={() => handleUnfollow(addr)}
                    title="Unfollow"
                  >
                    Unfollow
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Trending Topics */}
      <div className="info-card">
        <h3>What's happening</h3>
        <div className="trending-list">
          {trendingTopics.map((trend, index) => (
            <div key={index} className="trending-item">
              <div className="trending-category">Trending in Crypto</div>
              <div className="trending-topic">{trend.topic}</div>
              <div className="trending-posts">{trend.posts}</div>
            </div>
          ))}
        </div>
        <div className="show-more">
          <span>Show more</span>
        </div>
      </div>

      {/* Suggested Users */}
      <div className="info-card">
        <h3>Who to follow</h3>
        <div className="suggestions-list">
          {suggestedUsers.map((user, index) => (
            <div key={index} className="suggestion-item">
              <div className="suggestion-avatar">
                <span>👤</span>
              </div>
              <div className="suggestion-info">
                <div className="suggestion-name">
                  {user.name}
                  {user.verified && <span className="verified-badge">✓</span>}
                </div>
                <div className="suggestion-handle">{user.handle}</div>
              </div>
              <button className="follow-btn">Follow</button>
            </div>
          ))}
        </div>
        <div className="show-more">
          <span>Show more</span>
        </div>
      </div>

      {/* Footer Links */}
      <div className="footer-links">
        <div className="footer-row">
          <a href="#terms">Terms of Service</a>
          <a href="#privacy">Privacy Policy</a>
          <a href="#cookies">Cookie Policy</a>
        </div>
        <div className="footer-row">
          <a href="#accessibility">Accessibility</a>
          <a href="#ads">Ads info</a>
          <a href="#more">More...</a>
        </div>
        <div className="footer-copyright">
          © 2024 Znoospace on Solana
        </div>
      </div>
    </div>
  );
};

export default RightSidebar;
